import { Button, cn } from '@provenance/design';
import { Download, ExternalLink, Maximize2, Minimize2 } from 'lucide-react';
import { useState } from 'react';

/**
 * The document preview.
 *
 * Deliberately an <iframe> around the browser's own PDF viewer rather than pdf.js
 * rendered to a canvas. The trade is explicit:
 *
 *   What we give up — a bounding-box overlay drawn on top of the page, which §8.3
 *   describes as the ideal way to show a flagged field in place.
 *
 *   What we get — the browser's native viewer: real text selection, Ctrl+F, correct
 *   printing, correct rotation handling, no 350KB of pdf.js, and no worker to keep in
 *   step with a bundler. For a two-page certificate an operator reads once, that is the
 *   better exchange.
 *
 * The overlay is a Phase 2 item (todo/T-074): when it arrives, this component swaps to a
 * canvas renderer and nothing else in the review screen changes.
 */
export function PdfViewer({
  url,
  filename,
  pageCount,
  className,
}: {
  url: string | null;
  filename: string;
  pageCount: number | null;
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);

  /**
   * The URL the iframe actually loads, held until the *file* changes.
   *
   * The detail endpoint re-signs the URL on every fetch, and the review screen refetches
   * every two seconds while a check or stamping job is pending. Reloading on each new
   * signature made the PDF flash repeatedly. Identity is the path without the signing
   * query string, so the preview reloads when the file really changes — the stamped
   * certificate replacing the original — and not otherwise. An already-loaded document
   * does not care that its signature has since expired.
   */
  const file = url?.split('?')[0] ?? null;
  const [loaded, setLoaded] = useState({ file, url });
  if (loaded.file !== file) setLoaded({ file, url });

  if (!url) {
    return (
      <div
        className={cn(
          'grid place-items-center rounded-[var(--pv-radius-lg)] border border-dashed border-[var(--pv-line-strong)] bg-[var(--pv-card-alt)] p-10 text-center',
          className,
        )}
      >
        <p className="[font-size:13px] text-[var(--pv-muted)]">This document is not available to preview.</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-col overflow-hidden rounded-[var(--pv-radius-lg)] border border-[var(--pv-line)] bg-[var(--pv-card)]',
        className,
      )}
    >
      <div className="flex items-center gap-2 border-b border-[var(--pv-line)] bg-[var(--pv-paper-sunk)] px-3 py-2">
        <span className="min-w-0 flex-1 truncate [font-size:var(--pv-text-xs)] font-medium">{filename}</span>
        {pageCount !== null && (
          <span className="pv-num shrink-0 [font-size:var(--pv-text-2xs)] text-[var(--pv-faint)]">
            {pageCount} page{pageCount === 1 ? '' : 's'}
          </span>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setExpanded((v) => !v)}
          leadingIcon={expanded ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
        >
          {expanded ? 'Shrink' : 'Taller'}
        </Button>
        <Button variant="ghost" size="sm" asChild leadingIcon={<ExternalLink className="size-3.5" />}>
          <a href={url} target="_blank" rel="noreferrer">
            Open
          </a>
        </Button>
        <Button variant="ghost" size="icon" asChild>
          <a href={url} download={filename} aria-label={`Download ${filename}`}>
            <Download className="size-3.5" />
          </a>
        </Button>
      </div>

      <iframe
        key={loaded.file}
        src={`${loaded.url ?? url}#view=FitH`}
        title={`Preview of ${filename}`}
        className={cn(
          'w-full border-0 bg-[var(--pv-paper-sunk)]',
          expanded ? 'h-[calc(100vh-10rem)]' : 'h-[46rem]',
        )}
      />
    </div>
  );
}
